import { consultarPerfil, renovarToken, type FluxoLogin } from "@/lib/auth"
import { useSessionStore, type GrupoInfo } from "@/stores/sessionStore"

/**
 * Carregamento do perfil (/api/v1/me) para a store de sessão.
 * Compartilhado entre o bootstrap (SessionGate), o callback de login e o
 * restore silencioso da LoginPage — garante que roles/grupos estejam sempre
 * preenchidos logo após qualquer fluxo que estabeleça a sessão.
 */
export interface MeResponse {
  subject: string
  username: string
  email: string
  fullName: string
  /** Matrícula funcional (atributo `matricula` do Keycloak). */
  matricula?: string | null
  roles: string[]
  permissions: string[]
  groups: GrupoInfo[]
  /** INTERNO = usuário SIGI; CIDADAO = usuário gov.br sem permissões internas. */
  tipo?: "INTERNO" | "CIDADAO"
}

/** Busca /me e popula a store. Não lança (consultarPerfil degrada para null). */
export async function carregarPerfil(fluxo: FluxoLogin = "interno"): Promise<void> {
  const me = await consultarPerfil<MeResponse>()
  const store = useSessionStore.getState()
  store.setAuthenticated(true)
  const tipo =
    me?.tipo === "INTERNO"
      ? "INTERNO"
      : me?.tipo === "CIDADAO"
        ? "CIDADAO"
        : fluxo === "govbr"
          ? "CIDADAO"
          : "INTERNO"
  store.setTipo(tipo)
  if (tipo === "CIDADAO") {
    store.setProfile(me?.username ?? null, [], [])
    store.setIdentidade(me?.fullName ?? null, me?.email ?? null)
    store.setGroups([])
    store.setGrupoAtivo(null)
    return
  }
  if (me) {
    store.setProfile(me.username, me.roles, me.permissions)
    store.setIdentidade(me.fullName ?? null, me.email ?? null)
    store.setGroups(me.groups)
    // Grupo ativo (header X-Grupo): mantém a seleção atual da sessão se ainda
    // for válida; senão entra com o primeiro grupo por nome (sem persistência).
    const atual = store.grupoAtivo
    const atualizado = atual ? me.groups.find((g) => g.nome === atual.nome) : null
    if (atualizado) {
      // Mantém a seleção, mas atualiza modo/permissões efetivas do grupo.
      store.setGrupoAtivo(atualizado)
    } else {
      const primeiro =
        [...me.groups].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"))[0] ?? null
      store.setGrupoAtivo(primeiro)
    }
  }
}

/** Restaura a sessão a partir do refresh token (cookie HttpOnly) e carrega o perfil. */
export async function restaurarSessao(fluxo: FluxoLogin = "interno"): Promise<boolean> {
  const ok = await renovarToken(fluxo)
  if (!ok) {
    const store = useSessionStore.getState()
    store.setAuthenticated(false)
    store.setTipo(null)
    return false
  }
  await carregarPerfil(fluxo)
  return true
}

const restauracoes = new Map<FluxoLogin, Promise<boolean>>()

/**
 * Restauração única e compartilhada dentro da SPA, por fluxo (interno/govbr).
 * Diferente de {@link restaurarSessao}, chamadas concorrentes reaproveitam a
 * mesma requisição e o resultado não é repetido a cada montagem de rota.
 */
export function iniciarRestauracaoSessao(fluxo: FluxoLogin = "interno"): Promise<boolean> {
  const existente = restauracoes.get(fluxo)
  if (existente) return existente

  const promessa = restaurarSessao(fluxo).then((ok) => {
    // Falha não fica em cache: uma nova tentativa (ex.: após login em outra
    // aba ou quando o cookie passa a existir) pode restaurar a sessão.
    if (!ok) restauracoes.delete(fluxo)
    return ok
  })
  restauracoes.set(fluxo, promessa)
  return promessa
}
